import { useQuery } from '@tanstack/react-query';
import ContributeService from 'src/services/api/contribute';
import { ContributeTypes } from 'src/features/Contributions/config';
import { IContributeDetailTypeMapping, IContributeTypeMapping } from 'src/utils/typings/response/contribute';

export const QUERY_KEY_GET_CONTRIBUTE = 'QUERY_KEY_GET_CONTRIBUTE';
export const QUERY_KEY_GET_CONTRIBUTE_NOTES = 'QUERY_KEY_GET_CONTRIBUTE_NOTES';

export function useQueryGetContribute<T extends ContributeTypes>(
  type: T,
  query: Record<string, string | number>,
): { data: IResponseList<IContributeTypeMapping[T]> | undefined; isFetching: boolean } {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_GET_CONTRIBUTE, type, query],
    queryFn: () => ContributeService.getContributions(type, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!type,
    placeholderData: (previousData) => previousData,
  });

  return {
    data,
    isFetching,
  };
}

export function useQueryGetContributionById<T extends ContributeTypes>(
  type: T,
  id: string,
): { data: IResponse<IContributeDetailTypeMapping[T]> | undefined; isFetching: boolean } {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_GET_CONTRIBUTE, type, id],
    queryFn: () => ContributeService.getContributionById(type, id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!type,
    placeholderData: (previousData) => previousData,
  });

  return {
    data,
    isFetching,
  };
}

export function useQueryGetContributionNotes(id: string) {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_GET_CONTRIBUTE_NOTES, id],
    queryFn: () => ContributeService.getContributionNotes(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
  });

  return {
    data,
    isFetching,
    refetch,
  };
}
